import { useState, useRef, useCallback, MouseEvent, TouchEvent } from "react";

interface UseLongPressOptions {
  onLongPress: () => void;
  onClick?: () => void;
  threshold?: number;
}

export const useLongPress = ({
  onLongPress,
  onClick,
  threshold = 500,
}: UseLongPressOptions) => {
  const [longPressActive, setLongPressActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPress = useRef(false);

  const start = useCallback(() => {
    isLongPress.current = false;
    setLongPressActive(true);

    timerRef.current = setTimeout(() => {
      isLongPress.current = true;
      onLongPress();
      setLongPressActive(false);
    }, threshold);
  }, [onLongPress, threshold]);

  const clear = useCallback(
    (callOnClick = true) => {
      setLongPressActive(false);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      // Call onClick only if it wasn't a long press
      if (callOnClick && !isLongPress.current && onClick) {
        onClick();
      }
    },
    [onClick]
  );

  const handleMouseDown = useCallback(() => start(), [start]);
  const handleMouseUp = useCallback(() => clear(), [clear]);
  const handleMouseLeave = useCallback(() => clear(false), [clear]);

  const handleTouchStart = useCallback(() => start(), [start]);
  const handleTouchEnd = useCallback(() => clear(), [clear]);

  // Prevent context menu on long press (mobile)
  const handleContextMenu = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (longPressActive) {
        e.preventDefault();
      }
    },
    [longPressActive]
  );

  return {
    longPressProps: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onContextMenu: handleContextMenu,
    },
    isLongPressing: longPressActive,
  };
};
